import {useEffect, useMemo, useState} from 'react'
import {Card, Spinner, Button, Row, Col, Form} from 'react-bootstrap'
import {useQuery, useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {TWILIO_ACCOUNT_USAGE, TWILIO_NUMBERS_INVENTORY} from '../../gql/queries/twilioBilling'
import {RELEASE_TWILIO_NUMBER, ADD_NUMBERS_TO_POOL} from '../../gql/mutations/twilioBilling'
import {useTableSort} from '../../helpers/useTableSort'
import {US_STATES} from '../../helpers/catalogs'

type Category = {category: string; description: string; usage: string; usage_unit: string; price: number}
type TrendPoint = {month: string; total: number}
type Usage = {
  this_month_total: number
  last_month_total: number
  balance: number | null
  currency: string
  categories: Category[]
  trend: TrendPoint[]
}
type Classification = 'pool' | 'policydial' | 'messaging' | 'unclassified'
type InvNumber = {
  phone_number: string
  friendly_name: string
  type: string
  rental_estimate: number
  date_created: string
  owner: string
  classification: Classification
  last_used: string
  stale: boolean
}
type Inventory = {
  count: number
  total_rental_estimate: number
  counts: Record<string, number>
  unclassified_count: number
  stale_pool_count: number
  numbers: InvNumber[]
}

const money = (n: number | null | undefined) =>
  n == null ? '—' : `$${Number(n).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`

const CLASS_LABEL: Record<Classification, string> = {
  pool: 'Local-presence pool',
  policydial: 'PolicyDial (agent)',
  messaging: 'SMS / WhatsApp',
  unclassified: 'Unclassified',
}
const CLASS_BADGE: Record<Classification, string> = {
  pool: 'badge-light-primary',
  policydial: 'badge-light-success',
  messaging: 'badge-light-info',
  unclassified: 'badge-light-warning',
}

export const TwilioBillingPage = () => {
  const {data: uData, loading: uLoading, error: uError} = useQuery(TWILIO_ACCOUNT_USAGE, {fetchPolicy: 'cache-and-network'})
  const {data: iData, loading: iLoading, error: iError, refetch: refetchInv} = useQuery(TWILIO_NUMBERS_INVENTORY, {
    fetchPolicy: 'cache-and-network',
  })
  const [releaseNumber] = useMutation(RELEASE_TWILIO_NUMBER)
  const [addToPool, {loading: buyingPool}] = useMutation(ADD_NUMBERS_TO_POOL)

  const usage: Usage | undefined = uData?.twilioAccountUsage
  const inventory: Inventory | undefined = iData?.twilioNumbersInventory

  const [reviewOnly, setReviewOnly] = useState(false)
  const [classFilter, setClassFilter] = useState<Classification | ''>('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [busy, setBusy] = useState('')
  const PAGE_SIZE = 15

  const [poolState, setPoolState] = useState('')
  const [poolArea, setPoolArea] = useState('')
  const [poolQty, setPoolQty] = useState('1')

  const handleAddToPool = async () => {
    const qty = Math.max(1, Math.min(10, parseInt(poolQty || '1', 10) || 1))
    if (!poolState) {
      toast.warning('Pick a state first.')
      return
    }
    if (
      !window.confirm(
        `Buy ${qty} number(s) for the ${poolState} pool? This spends ~$${(qty * 1.15).toFixed(2)} on Twilio.`
      )
    )
      return
    try {
      const {data} = await addToPool({
        variables: {state: poolState, areaCode: poolArea || null, quantity: qty},
      })
      const res = data?.addNumbersToPool
      if (res?.success) {
        toast.success(res.message || 'Numbers added to the pool.')
        ;(res.warnings || []).forEach((w: string) => toast.warning(w))
        setPoolArea('')
        await refetchInv()
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not add numbers to the pool')
    }
  }
  const {onSort, sortRows, arrow} = useTableSort<any>('', 'desc')

  const filtered = useMemo(() => {
    let list = inventory?.numbers ?? []
    if (classFilter) list = list.filter((n) => n.classification === classFilter)
    if (reviewOnly) list = list.filter((n) => n.classification === 'unclassified' || n.stale)
    const q = search.replace(/\D/g, '')
    if (q) list = list.filter((n) => n.phone_number.replace(/\D/g, '').includes(q))
    return sortRows(list)
  }, [inventory, reviewOnly, search, classFilter, sortRows])

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => {
    setPage(1)
  }, [search, reviewOnly, classFilter])

  const mom = useMemo(() => {
    if (!usage || !usage.last_month_total) return null
    return ((usage.this_month_total - usage.last_month_total) / usage.last_month_total) * 100
  }, [usage])
  const trendMax = useMemo(() => Math.max(1, ...(usage?.trend ?? []).map((t) => t.total)), [usage])

  const handleRelease = async (n: InvNumber) => {
    const warn =
      n.classification === 'messaging'
        ? '\n\n⚠️ This number is used for SMS/WhatsApp. Releasing it will break that.'
        : n.classification === 'pool'
        ? '\n\nIt will be removed from the local-presence dialing pool.'
        : ''
    if (!window.confirm(`Release ${n.phone_number}? This permanently gives up the number on Twilio and stops its ~$${n.rental_estimate}/mo charge.${warn}`))
      return
    setBusy(n.phone_number)
    try {
      const {data} = await releaseNumber({variables: {phoneNumber: n.phone_number}})
      if (data?.releaseTwilioNumber?.success) {
        toast.success(`${n.phone_number} released.`)
        ;(data.releaseTwilioNumber.warnings || []).forEach((w: string) => toast.warning(w))
        await refetchInv()
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not release the number')
    } finally {
      setBusy('')
    }
  }

  const exportCsv = () => {
    const rows = inventory?.numbers ?? []
    if (!rows.length) {
      toast.info('Nothing to export yet.')
      return
    }
    const header = ['Phone Number', 'Friendly Name', 'Type', 'Monthly Rental (est.)', 'Purchased', 'Used For', 'Owner', 'Last Used', 'Stale']
    const body = rows.map((n) => [
      n.phone_number,
      `"${(n.friendly_name || '').replace(/"/g, '""')}"`,
      n.type,
      n.rental_estimate,
      n.date_created,
      n.classification,
      n.owner || '',
      n.last_used || '',
      n.stale ? 'yes' : 'no',
    ])
    const csv = [header.join(','), ...body.map((r) => r.join(','))].join('\n')
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'})
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'twilio-numbers.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  if (uError || iError) {
    return (
      <Card className='mt-5'>
        <Card.Body className='text-center py-10'>
          <h4 className='mb-2'>Twilio billing unavailable</h4>
          <p className='text-muted mb-0'>{uError?.message || iError?.message}</p>
        </Card.Body>
      </Card>
    )
  }

  const lowBalance = usage?.balance != null && usage.balance < 50

  return (
    <div className='mt-5'>
      <div className='mb-5'>
        <h2 className='mb-1'>Billing</h2>
        <span className='text-muted'>Spend and phone-number inventory for the organization.</span>
      </div>

      <Row className='g-4 mb-2'>
        <Col md={3} sm={6}>
          <Card className='h-100'><Card.Body>
            <div className='text-muted fs-7 text-uppercase'>This month</div>
            <div className='fs-2 fw-bold'>{uLoading && !usage ? <Spinner size='sm' animation='border' /> : money(usage?.this_month_total)}</div>
            {mom != null && (
              <span className={`badge ${mom > 0 ? 'badge-light-danger' : 'badge-light-success'}`}>
                {mom > 0 ? '▲' : '▼'} {Math.abs(mom).toFixed(0)}% vs last month
              </span>
            )}
          </Card.Body></Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className='h-100'><Card.Body>
            <div className='text-muted fs-7 text-uppercase'>Last month</div>
            <div className='fs-2 fw-bold'>{money(usage?.last_month_total)}</div>
          </Card.Body></Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className='h-100'><Card.Body>
            <div className='text-muted fs-7 text-uppercase'>Balance</div>
            <div className={`fs-2 fw-bold ${lowBalance ? 'text-danger' : ''}`}>{money(usage?.balance)}</div>
            {lowBalance && <span className='badge badge-light-danger'>Low balance</span>}
          </Card.Body></Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className='h-100'><Card.Body>
            <div className='text-muted fs-7 text-uppercase'>Numbers</div>
            <div className='fs-2 fw-bold'>{inventory?.count ?? (iLoading ? '…' : 0)}</div>
            <span className='text-muted fs-8'>~{money(inventory?.total_rental_estimate)}/mo rental</span>
          </Card.Body></Card>
        </Col>
      </Row>

      <Row className='g-4'>
        <Col lg={6}>
          <Card className='h-100'>
            <Card.Header><Card.Title>Monthly spend</Card.Title></Card.Header>
            <Card.Body>
              {!usage ? <Spinner animation='border' size='sm' /> : (usage.trend || []).map((t) => (
                <div key={t.month} className='d-flex align-items-center mb-3'>
                  <span className='text-muted fs-8' style={{width: 64}}>{t.month}</span>
                  <div className='flex-grow-1 mx-3 bg-light rounded' style={{height: 14}}>
                    <div className='bg-primary rounded' style={{height: 14, width: `${(t.total / trendMax) * 100}%`}} />
                  </div>
                  <span className='fw-semibold fs-7' style={{width: 80, textAlign: 'right'}}>{money(t.total)}</span>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className='h-100'>
            <Card.Header><Card.Title>This month by category</Card.Title></Card.Header>
            <Card.Body className='p-0'>
              <div className='table-responsive'>
                <table className='table table-row-dashed align-middle gy-2 mb-0'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th className='ps-6'>Category</th><th>Usage</th><th className='text-end pe-6'>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(usage?.categories ?? []).map((c) => (
                      <tr key={c.category}>
                        <td className='ps-6'>{c.description || c.category}</td>
                        <td className='text-muted'>{c.usage} {c.usage_unit}</td>
                        <td className='text-end pe-6 fw-semibold'>{money(c.price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add numbers to the local-presence pool */}
      <Card className='mt-4'>
        <Card.Header>
          <Card.Title>Add numbers to the pool</Card.Title>
        </Card.Header>
        <Card.Body>
          <div className='text-muted fs-7 mb-3'>
            Buy local-presence numbers and add them to VICIdial's outbound Caller-ID pool
            (rotated by the lead's state). No burned number required.
          </div>
          <Row className='g-3 align-items-end'>
            <Col xs='auto'>
              <Form.Label className='small text-muted mb-0'>State</Form.Label>
              <Form.Select
                size='sm'
                style={{minWidth: 180}}
                value={poolState}
                onChange={(e) => setPoolState(e.target.value)}
              >
                <option value=''>Select a state…</option>
                {US_STATES.map((s: any) => (
                  <option key={s.code || s.value} value={s.code || s.value}>
                    {s.name || s.label} ({s.code || s.value})
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs='auto'>
              <Form.Label className='small text-muted mb-0'>Area code (optional)</Form.Label>
              <Form.Control
                size='sm'
                style={{maxWidth: 130}}
                placeholder='e.g. 305'
                value={poolArea}
                onChange={(e) => setPoolArea(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
              />
            </Col>
            <Col xs='auto'>
              <Form.Label className='small text-muted mb-0'>Quantity</Form.Label>
              <Form.Control
                size='sm'
                type='number'
                min={1}
                max={10}
                style={{maxWidth: 90}}
                value={poolQty}
                onChange={(e) => setPoolQty(e.target.value)}
              />
            </Col>
            <Col xs='auto'>
              <Button variant='primary' onClick={handleAddToPool} disabled={buyingPool || !poolState}>
                {buyingPool ? <Spinner animation='border' size='sm' /> : 'Buy & add to pool'}
              </Button>
            </Col>
            <Col>
              <span className='text-muted fs-8'>
                Est. cost ≈ ${(Math.max(1, Math.min(10, parseInt(poolQty || '1', 10) || 1)) * 1.15).toFixed(2)}
                {' '}(max 10 per purchase).
              </span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Numbers inventory + cleanup */}
      <Card className='mt-4'>
        <Card.Header className='d-flex align-items-center justify-content-between flex-wrap gap-2'>
          <div>
            <Card.Title className='mb-1'>Numbers inventory {inventory ? `(${inventory.count})` : ''}</Card.Title>
            {inventory && (
              <div className='d-flex flex-wrap gap-2 align-items-center'>
                {(['pool', 'messaging', 'policydial', 'unclassified'] as Classification[]).map((k) =>
                  inventory.counts?.[k] ? (
                    <span
                      key={k}
                      role='button'
                      title={classFilter === k ? 'Clear filter' : `Filter by ${CLASS_LABEL[k]}`}
                      onClick={() => setClassFilter((prev) => (prev === k ? '' : k))}
                      className={`badge ${CLASS_BADGE[k]}`}
                      style={{
                        cursor: 'pointer',
                        outline: classFilter === k ? '2px solid currentColor' : 'none',
                        opacity: classFilter && classFilter !== k ? 0.45 : 1,
                      }}
                    >
                      {CLASS_LABEL[k]}: {inventory.counts[k]}
                    </span>
                  ) : null
                )}
                {inventory.stale_pool_count > 0 && (
                  <span className='badge badge-light-warning'>Stale pool: {inventory.stale_pool_count}</span>
                )}
                {classFilter && (
                  <a className='fs-8 text-primary' role='button' onClick={() => setClassFilter('')}>
                    Clear filter
                  </a>
                )}
              </div>
            )}
          </div>
          <div className='d-flex align-items-center gap-3 flex-wrap'>
            <Form.Control
              size='sm'
              type='search'
              placeholder='Search number…'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{maxWidth: 180}}
            />
            <Form.Check
              type='switch'
              id='reviewOnly'
              label='Review candidates only'
              checked={reviewOnly}
              onChange={(e) => setReviewOnly(e.target.checked)}
            />
            <Button variant='light-primary' size='sm' onClick={exportCsv}>Export CSV</Button>
          </div>
        </Card.Header>
        <Card.Body className='p-0'>
          {iLoading && !inventory ? (
            <div className='p-6'><Spinner animation='border' size='sm' /></div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-row-dashed align-middle gy-2 mb-0'>
                <thead>
                  <tr className='fw-bold text-muted' style={{cursor: 'pointer'}}>
                    <th className='ps-6' onClick={() => onSort('phone_number')}>Number{arrow('phone_number')}</th>
                    <th onClick={() => onSort('type')}>Type{arrow('type')}</th>
                    <th onClick={() => onSort('rental_estimate')}>Rental/mo{arrow('rental_estimate')}</th>
                    <th onClick={() => onSort('classification')}>Used for{arrow('classification')}</th>
                    <th onClick={() => onSort('owner')}>Owner / Last used{arrow('owner')}</th>
                    <th className='text-end pe-6'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((n) => (
                    <tr key={n.phone_number}>
                      <td className='ps-6 fw-bold'>{n.phone_number}</td>
                      <td>
                        <span className={`badge ${n.type === 'tollfree' ? 'badge-light-info' : 'badge-light-secondary'}`}>{n.type}</span>
                      </td>
                      <td>{money(n.rental_estimate)}</td>
                      <td>
                        <span className={`badge ${CLASS_BADGE[n.classification]}`}>{CLASS_LABEL[n.classification]}</span>
                        {n.stale && <span className='badge badge-light-warning ms-1'>stale</span>}
                      </td>
                      <td className='text-muted'>
                        {n.owner || (n.last_used ? `used ${n.last_used.slice(0, 10)}` : <span className='text-muted'>—</span>)}
                      </td>
                      <td className='text-end pe-6'>
                        <Button
                          variant='light-danger'
                          size='sm'
                          disabled={busy === n.phone_number}
                          onClick={() => handleRelease(n)}
                        >
                          {busy === n.phone_number ? <Spinner animation='border' size='sm' /> : 'Release'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(pageCount > 1 || filtered.length === 0) && (
                <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 px-6 py-3'>
                  <span className='text-muted fs-8'>
                    {filtered.length === 0
                      ? 'No numbers match'
                      : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filtered.length)} of ${filtered.length}`}
                  </span>
                  {pageCount > 1 && (
                    <div className='d-flex align-items-center gap-2'>
                      <Button variant='light' size='sm' disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                        Previous
                      </Button>
                      <span className='text-muted fs-8'>{page} / {pageCount}</span>
                      <Button variant='light' size='sm' disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}
